import React, { useState, useEffect, useRef } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import styles from './admin.module.css';
import PAGINATION_CONFIG from '../../config/paginationConfig';

/**
 * SearchableInput component for Admin
 * Provides debounced search functionality for user filtering
 */
const SearchableInput = ({
   onSearch,
   placeholder = "Search users...",
   debounceDelay = PAGINATION_CONFIG.SEARCH_DEBOUNCE_DELAY,
   className = "",
   size = "small",
   fullWidth = true,
   initialValue = ""
}) => {
   const [searchValue, setSearchValue] = useState(initialValue);
   const [debouncedValue, setDebouncedValue] = useState(initialValue);
   const previousDebouncedValue = useRef(initialValue);
   
   // Inline debounce logic
   useEffect(() => {
      const timer = setTimeout(() => {
         setDebouncedValue(searchValue);
      }, debounceDelay);

      return () => {
         clearTimeout(timer);
      };
   }, [searchValue, debounceDelay]);

   // Call onSearch only when debounced value actually changes
   useEffect(() => {
      if (debouncedValue !== previousDebouncedValue.current) {
         console.log('SearchableInput: Calling onSearch due to value change', {
            previousValue: previousDebouncedValue.current,
            newValue: debouncedValue
         });
         onSearch(debouncedValue);
         previousDebouncedValue.current = debouncedValue;
      }
   }, [debouncedValue, onSearch]);

   const handleSearchChange = (event) => {
      setSearchValue(event.target.value);
   };

   const handleClearSearch = () => {
      setSearchValue('');
   };

   return (
      <TextField
         value={searchValue}
         onChange={handleSearchChange}
         placeholder={placeholder}
         size={size}
         fullWidth={fullWidth}
         className={`${className} ${styles.searchFieldRoot}`}
         variant="outlined"
         InputProps={{
            startAdornment: (
               <InputAdornment position="start">
                  <SearchIcon color="action" />
               </InputAdornment>
            ),
            endAdornment: searchValue && (
               <InputAdornment position="end">
                  <IconButton
                     size="small"
                     onClick={handleClearSearch}
                     edge="end"
                  >
                     <ClearIcon />
                  </IconButton>
               </InputAdornment>
            )
         }}
      />
   );
};

export default SearchableInput;
